import { defineStore } from 'pinia'
import { getAnalytics, initializeAnalytics, isSupported, logEvent, setUserId as gaSetUserId, setUserProperties, type Analytics } from 'firebase/analytics'
import { type FirebaseApp } from 'firebase/app'
import { getFirebaseApp } from '@/plugins/firestore'
import { logger } from '@/utils/logger'


interface AnalyticsState {
    analytics: Analytics | null
    initialized: boolean
    supported: boolean | null
    enabled: boolean
    userId: string | null
}

async function initAnalytics(): Promise<Analytics | null> {
    try {
        if (!(await isSupported())) return null
        // Ensure the Firebase app is initialized before Analytics
        const app: FirebaseApp = await getFirebaseApp
        // Disable automatic page_view so our router-driven events always include user_id
        try {
            return initializeAnalytics(app, { config: { send_page_view: false } })
        } catch {
            // If Analytics was already initialized elsewhere, fall back to retrieving the instance
            logger.info('Analytics already initialized elsewhere, falling back to retrieving the instance')
            return getAnalytics(app)
        }
    } catch (err) {
        logger.warn('Analytics init failed:', err)
        return null
    }
}

export const useAnalyticsStore = defineStore('analytics', {
    state: (): AnalyticsState => ({ analytics: null, initialized: false, supported: null, enabled: true, userId: null }),
    actions: {
        async ensureInitialized() {
            if (this.initialized) return
            this.analytics = await initAnalytics()
            this.supported = this.analytics != null
            this.initialized = true
        },
        async trackPageView(url: string, title?: string) {
            await this.ensureInitialized()
            if (!this.enabled || !this.analytics) return
            const page_path = new URL(url, window.location.origin).pathname
            const event_params = {
                page_location: url,
                page_path: page_path,
                page_title: title ?? page_path,
            }
            logEvent(this.analytics, 'page_view', event_params)
            logger.debug('page_view', event_params)
        },
        async trackEvent(name: string, params?: Record<string, unknown>) {
            await this.ensureInitialized()
            if (!this.enabled || !this.analytics) return
            logEvent(this.analytics, name, params as Record<string, string | number | boolean | null | undefined>)
        },
        async setUserId(id: string | null) {
            await this.ensureInitialized()
            this.userId = id ?? null
            if (!this.analytics) return
            gaSetUserId(this.analytics, this.userId)
            logger.debug('setUserId', this.userId)
        },
        async setUserProperties(props: Record<string, string | number | boolean | null | undefined>) {
            await this.ensureInitialized()
            if (!this.analytics) return
            setUserProperties(this.analytics, props as Record<string, string>)
        },
        setEnabled(enabled: boolean) {
            this.enabled = enabled
        },
    },
})


